"use client"

import React, { useEffect, useMemo, useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { FilterSearchHomesResponse } from "@/features/homeowners/hooks/filter-search-homes.hook"
import { useSession } from "@/features/auth/context/session.context"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { getClaimedByDisplayName } from "@/features/homeowners/lib/claimed-by-display"
import { UserRole } from "@/shared/constants/user-roles"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { Button } from "@/shared/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"

type ClaimedByWorkscopeStatusProps = {
  allHomesData?: FilterSearchHomesResponse
}

type ClaimedByOption = {
  label: string
  value: string
}

type WorkscopeDefinition = {
  label: string
  statusPath: string
  claimedByPath: string
}

type WorkscopeStatusBucket = "complete" | "inProgress" | "notStarted" | "unknown"

type ClaimedWorkscopeItem = {
  organization: string
  homeId: number
  homeownerUserId: number | null
  homeownerName: string
  address: string
  workscope: string
  status: string
  bucket: WorkscopeStatusBucket
}

type OrganizationSummary = {
  organization: string
  homes: number
  total: number
  complete: number
  inProgress: number
  notStarted: number
  unknown: number
  percentComplete: number
  items: ClaimedWorkscopeItem[]
}

const ALL_CLAIMED_BY = "__all__"

const WORKSCOPE_DEFINITIONS: WorkscopeDefinition[] = [
  { label: "Tree", statusPath: "treeDebrisInfo.treeWorkNeeded", claimedByPath: "treeDebrisInfo.treeClaimedBy" },
  { label: "Exterior Debris", statusPath: "treeDebrisInfo.exteriorDebrisRemovalNeeded", claimedByPath: "treeDebrisInfo.debrisClaimedBy" },
  { label: "Heavy Equipment", statusPath: "treeDebrisInfo.heavyEquipmentNeeded", claimedByPath: "treeDebrisInfo.heavyClaimedBy" },
  { label: "Roof Tarp", statusPath: "roofingInfo.roofTarpNeeded", claimedByPath: "roofingInfo.roofTarpClaimedBy" },
  { label: "Roofing", statusPath: "roofingInfo.roofing", claimedByPath: "roofingInfo.roofingClaimedBy" },
  { label: "Muck & Gut", statusPath: "muckGutInfo.muckGutNeeded", claimedByPath: "muckGutInfo.claimedBy" },
  { label: "Mold Remediation", statusPath: "moldRemediationInfo.moldRemediation", claimedByPath: "moldRemediationInfo.claimedBy" },
  { label: "Rebuild Prep", statusPath: "rebuildPrepInfo.rebuildPrep", claimedByPath: "rebuildPrepInfo.claimedBy" },
  { label: "Rough Carpentry", statusPath: "roughCarpentryInfo.roughCarpentry", claimedByPath: "roughCarpentryInfo.claimedBy" },
  { label: "Electrical", statusPath: "electricalInfo.electrical", claimedByPath: "electricalInfo.claimedBy" },
  { label: "Rough Electrical", statusPath: "electricalInfo.electricalRoughIn", claimedByPath: "electricalInfo.electricalRoughInClaimedBy" },
  { label: "HVAC", statusPath: "hvacInfo.hvac", claimedByPath: "hvacInfo.claimedBy" },
  { label: "Rough Mechanical", statusPath: "hvacInfo.mechanicalRoughIn", claimedByPath: "hvacInfo.mechanicalRoughInClaimedBy" },
  { label: "Plumbing", statusPath: "plumbingInfo.plumbing", claimedByPath: "plumbingInfo.claimedBy" },
  { label: "Rough Plumbing", statusPath: "plumbingInfo.plumbingRoughIn", claimedByPath: "plumbingInfo.plumbingRoughInClaimedBy" },
  { label: "Crawl Space Muck", statusPath: "crawlSpaceInfo.crawlSpaceMuck", claimedByPath: "crawlSpaceInfo.crawlSpaceMuckClaimedBy" },
  { label: "Crawl Space Insulation", statusPath: "crawlSpaceInfo.crawlSpaceInsulation", claimedByPath: "crawlSpaceInfo.crawlSpaceInsulationClaimedBy" },
  { label: "Insulation", statusPath: "insulationInstallInfo.insulationInstall", claimedByPath: "insulationInstallInfo.claimedBy" },
  { label: "Drywall Hanging", statusPath: "drywallHangingInfo.drywallHanging", claimedByPath: "drywallHangingInfo.claimedBy" },
  { label: "Mud & Tape", statusPath: "mudAndTapeInfo.mudAndTape", claimedByPath: "mudAndTapeInfo.claimedBy" },
  { label: "Bathroom Rebuild", statusPath: "bathroomRebuildInfo.bathroomRebuild", claimedByPath: "bathroomRebuildInfo.claimedBy" },
  { label: "Cabinet Install", statusPath: "cabinetInstallInfo.cabinetInstall", claimedByPath: "cabinetInstallInfo.cabinetClaimedBy" },
  { label: "Counter Install", statusPath: "cabinetInstallInfo.counterInstall", claimedByPath: "cabinetInstallInfo.counterClaimedBy" },
  { label: "Appliance Install", statusPath: "cabinetInstallInfo.applianceInstall", claimedByPath: "cabinetInstallInfo.applianceClaimedBy" },
  { label: "Interior Paint", statusPath: "interiorPaintInfo.interiorPaint", claimedByPath: "interiorPaintInfo.claimedBy" },
  { label: "Interior Doors", statusPath: "installInteriorDoorsInfo.installInteriorDoors", claimedByPath: "installInteriorDoorsInfo.claimedBy" },
  { label: "Trim Install", statusPath: "trimInstallInfo.trimInstall", claimedByPath: "trimInstallInfo.claimedBy" },
  { label: "Flooring Install", statusPath: "flooringInstallInfo.flooringInstall", claimedByPath: "flooringInstallInfo.claimedBy" },
  { label: "Exterior Door Install", statusPath: "exteriorDoorInstallInfo.exteriorDoorInstall", claimedByPath: "exteriorDoorInstallInfo.claimedBy" },
  { label: "Shutter Install", statusPath: "stormShutters.shutterInstall", claimedByPath: "stormShutters.shuttersClaimedBy" },
  { label: "Driveway Repair", statusPath: "drivewayRepair.drivewayRepair", claimedByPath: "drivewayRepair.drivewayClaimedBy" },
  { label: "Miscellaneous", statusPath: "miscellaneousInfo.miscellaneous", claimedByPath: "miscellaneousInfo.claimedBy" },
]

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((current: unknown, key) => (current as Record<string, unknown>)?.[key], obj)
}

function normalizeClaimedBy(raw: unknown): string[] {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw.flatMap(normalizeClaimedBy)
  if (typeof raw !== "string") return []

  const value = raw.trim()
  if (!value) return []

  try {
    return normalizeClaimedBy(JSON.parse(value))
  } catch {
    return [value]
  }
}

function normalizeOrganizationName(value: unknown): string {
  return getClaimedByDisplayName(value).toLowerCase()
}

function getHomeownerName(home: any): string {
  const firstName = home.homeowner?.firstName || home.homeowner?.first_name || ""
  const lastName = home.homeowner?.lastName || home.homeowner?.last_name || ""
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || home.homeowner?.user?.fullname || home.homeowner?.name || `Home #${home.id}`
}

function getHomeownerUserId(home: any): number | null {
  const rawUserId = home.homeowner?.user?.id ?? home.homeowner?.userId ?? null
  const userId = Number(rawUserId)
  return Number.isFinite(userId) ? userId : null
}

function bucketStatus(status: string): WorkscopeStatusBucket {
  if (status === "Complete" || status === "Materials Only Complete") return "complete"
  if (["25%", "50%", "75%", "Materials Only"].includes(status)) return "inProgress"
  if (status === "Needed Not Started" || status === "Reassessment Needed") return "notStarted"
  return "unknown"
}

function buildClaimedWorkscopeItems(homes: any[]): ClaimedWorkscopeItem[] {
  return homes.flatMap((home) => {
    return WORKSCOPE_DEFINITIONS.flatMap((definition) => {
      const claimedByValues = normalizeClaimedBy(getNestedValue(home, definition.claimedByPath))
        .filter((value) => value !== "No Need")
      if (claimedByValues.length === 0) return []

      const rawStatus = getNestedValue(home, definition.statusPath)
      const status = typeof rawStatus === "string" && rawStatus.trim()
        ? rawStatus.trim()
        : "Unknown"

      if (status === "No Assistance Required") return []

      return claimedByValues.map((organization) => ({
        organization: getClaimedByDisplayName(organization),
        homeId: home.id,
        homeownerUserId: getHomeownerUserId(home),
        homeownerName: getHomeownerName(home),
        address: home.address || "",
        workscope: definition.label,
        status,
        bucket: bucketStatus(status),
      }))
    })
  })
}

function summarizeByOrganization(items: ClaimedWorkscopeItem[]): OrganizationSummary[] {
  const summaries = new Map<string, OrganizationSummary>()

  items.forEach((item) => {
    const summary = summaries.get(item.organization) ?? {
      organization: item.organization,
      homes: 0,
      total: 0,
      complete: 0,
      inProgress: 0,
      notStarted: 0,
      unknown: 0,
      percentComplete: 0,
      items: [],
    }

    summary.total += 1
    summary.items.push(item)
    if (item.bucket === "complete") summary.complete += 1
    if (item.bucket === "inProgress") summary.inProgress += 1
    if (item.bucket === "notStarted") summary.notStarted += 1
    if (item.bucket === "unknown") summary.unknown += 1
    summaries.set(item.organization, summary)
  })

  return Array.from(summaries.values())
    .map((summary) => ({
      ...summary,
      homes: new Set(summary.items.map((item) => item.homeId)).size,
      percentComplete: summary.total > 0 ? Math.round((summary.complete / summary.total) * 100) : 0,
      items: summary.items.sort((a, b) => a.homeownerName.localeCompare(b.homeownerName)),
    }))
    .sort((a, b) => a.organization.localeCompare(b.organization))
}

function getVisibleDetailItems(summary: OrganizationSummary, role: UserRole | undefined, sessionSub: unknown, profileOrganizationName: unknown): ClaimedWorkscopeItem[] {
  if (role === UserRole.ADMIN || role === UserRole.BASE_LEADER) {
    return summary.items
  }

  if (role === UserRole.PARTNER) {
    const userOrganization = normalizeOrganizationName(profileOrganizationName)
    return userOrganization && userOrganization === normalizeOrganizationName(summary.organization)
      ? summary.items
      : []
  }

  if (role === UserRole.HOMEOWNER) {
    const userId = Number(sessionSub)
    if (!Number.isFinite(userId)) return []
    return summary.items.filter((item) => item.homeownerUserId === userId)
  }

  return []
}

export default function ClaimedByWorkscopeStatus({ allHomesData }: ClaimedByWorkscopeStatusProps) {
  const { session, profile } = useSession()
  const { regionId } = useRegionId()
  const [claimedByOptions, setClaimedByOptions] = useState<ClaimedByOption[]>([
    { label: "Unclaimed", value: "Unclaimed" },
    { label: "No Need", value: "No Need" },
    { label: "Homeowner", value: "Homeowner" },
  ])
  const [claimedByLoading, setClaimedByLoading] = useState(false)
  const [selectedClaimedBy, setSelectedClaimedBy] = useState(ALL_CLAIMED_BY)
  const [expandedOrganizations, setExpandedOrganizations] = useState<Set<string>>(new Set())

  useEffect(() => {
    let isMounted = true

    const fetchClaimedByOptions = async () => {
      setClaimedByLoading(true)
      try {
        const params = new URLSearchParams()
        if (regionId) {
          params.set("regionId", regionId.toString())
        }

        const url = `/organizations/options${params.toString() ? `?${params.toString()}` : ""}`
        const res = await authFetchJson<any[]>(url)
        if (!res.success) return
        if (!isMounted) return

        const data = (res.data as any)?.data ?? res.data
        const organizations = Array.isArray(data) ? data : []

        setClaimedByOptions([
          { label: "Unclaimed", value: "Unclaimed" },
          { label: "No Need", value: "No Need" },
          { label: "Homeowner", value: "Homeowner" },
          ...Array.from(
            new Set(organizations.map((org: any) => getClaimedByDisplayName(org.label)))
          ).map((orgName) => ({
            label: orgName,
            value: orgName,
          })),
        ])
      } finally {
        if (isMounted) {
          setClaimedByLoading(false)
        }
      }
    }

    fetchClaimedByOptions()

    return () => {
      isMounted = false
    }
  }, [regionId])

  const summaries = useMemo(() => {
    if (!allHomesData?.success || !Array.isArray(allHomesData.data)) return []
    const activeHomes = allHomesData.data.filter((home: any) => home.isReported === true)
    const items = buildClaimedWorkscopeItems(activeHomes)
    const filteredItems = selectedClaimedBy === ALL_CLAIMED_BY
      ? items
      : items.filter((item) => item.organization === getClaimedByDisplayName(selectedClaimedBy))
    return summarizeByOrganization(filteredItems)
  }, [allHomesData, selectedClaimedBy])

  const totalClaimed = summaries.reduce((sum, summary) => sum + summary.total, 0)

  const toggleExpanded = (organization: string) => {
    setExpandedOrganizations((current) => {
      const next = new Set(current)
      if (next.has(organization)) {
        next.delete(organization)
      } else {
        next.add(organization)
      }
      return next
    })
  }

  if (!allHomesData) {
    return <p className="py-8 text-center text-muted-foreground">Loading claimed-by workscope status...</p>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          {session?.role !== UserRole.ADMIN && session?.role !== UserRole.BASE_LEADER && (
            <p className="text-xs text-muted-foreground">
              Homeowner detail is limited by role; summary totals remain visible.
            </p>
          )}
        </div>
        <div className="w-full md:w-72">
          <label className="mb-1 block text-sm font-medium">Claimed By</label>
          <Select value={selectedClaimedBy} onValueChange={setSelectedClaimedBy}>
            <SelectTrigger size="sm">
              <SelectValue placeholder="All Claimed By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CLAIMED_BY}>All Claimed By</SelectItem>
              {claimedByOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {claimedByLoading && <p className="mt-1 text-xs text-muted-foreground">Loading organizations...</p>}
        </div>
      </div>

      {summaries.length === 0 ? (
        <p className="rounded-md border border-dashed p-6 text-center text-muted-foreground">
          No claimed workscopes found for this selection.
        </p>
      ) : (
        <Table className="w-full table-fixed text-xs sm:min-w-[720px] sm:table-auto sm:text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-20 w-[58%] bg-muted px-1 sm:w-auto sm:min-w-40 sm:px-2">Claimed By</TableHead>
              <TableHead className="w-[21%] px-1 text-right sm:w-auto sm:px-2">WS Total</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Complete</TableHead>
              <TableHead className="hidden text-right sm:table-cell">In Progress</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Not Started</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Unknown</TableHead>
              <TableHead className="hidden text-right sm:table-cell">% Complete</TableHead>
              <TableHead className="w-[21%] px-1 text-right sm:w-auto sm:px-2">Homes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summaries.map((summary) => {
              const detailItems = getVisibleDetailItems(summary, session?.role, session?.sub, profile?.organizationName)
              const canExpand = detailItems.length > 0
              const isExpanded = canExpand && expandedOrganizations.has(summary.organization)
              return (
                <React.Fragment key={summary.organization}>
                  <TableRow>
                    <TableCell className="sticky left-0 z-10 w-[58%] bg-muted px-1 sm:w-auto sm:min-w-40 sm:px-2">
                      {canExpand ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto max-w-full justify-start px-0 text-xs font-semibold sm:text-sm"
                          onClick={() => toggleExpanded(summary.organization)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="mr-1 h-3 w-3 shrink-0 sm:h-4 sm:w-4" />
                          ) : (
                            <ChevronRight className="mr-1 h-3 w-3 shrink-0 sm:h-4 sm:w-4" />
                          )}
                          <span className="truncate">{summary.organization}</span>
                        </Button>
                      ) : (
                        <span className="block truncate font-semibold">{summary.organization}</span>
                      )}
                    </TableCell>
                    <TableCell className="w-[21%] px-1 text-right sm:w-auto sm:px-2">{summary.total}</TableCell>
                    <TableCell className="hidden text-right text-green-700 sm:table-cell">{summary.complete}</TableCell>
                    <TableCell className="hidden text-right sm:table-cell">{summary.inProgress}</TableCell>
                    <TableCell className="hidden text-right sm:table-cell">{summary.notStarted}</TableCell>
                    <TableCell className="hidden text-right sm:table-cell">{summary.unknown}</TableCell>
                    <TableCell className="hidden text-right font-medium sm:table-cell">{summary.percentComplete}%</TableCell>
                    <TableCell className="w-[21%] px-1 text-right sm:w-auto sm:px-2">{summary.homes}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={8} className="bg-muted/30 p-0">
                        <div className="max-h-80 overflow-auto p-3">
                          <Table noScrollWrapper>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Homeowner</TableHead>
                                <TableHead>Address</TableHead>
                                <TableHead>Workscope</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detailItems.map((item, index) => (
                                <TableRow key={`${summary.organization}-${item.homeId}-${item.workscope}-${index}`}>
                                  <TableCell>{item.homeownerName}</TableCell>
                                  <TableCell>{item.address}</TableCell>
                                  <TableCell>{item.workscope}</TableCell>
                                  <TableCell>{item.status}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
